import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const moods = [
  {
    id: 1,
    name: 'Quiet Minimal',
    tagline: 'Where Less Becomes Enough',
    description: 'Soft light, clean lines, and intentional emptiness. A space that removes distraction and brings clarity to everyday living.',
    designLanguage: ['Warm whites, soft greys', 'Hidden storage', 'Matte finishes'],
    image: 'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800&q=80',
    color: '#F5F3F0',
  },
  {
    id: 2,
    name: 'Warm Contemporary',
    tagline: 'Modern Living, Made Inviting',
    description: 'Contemporary forms softened by warmth. Wood, fabric, and layered lighting create a space that feels both current and comfortable.',
    designLanguage: ['Veneer finishes', 'Neutral palettes with depth', 'Balanced contrast'],
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    color: '#E8DFD3',
  },
  {
    id: 3,
    name: 'Crafted Classic',
    tagline: 'Timeless, With a Human Touch',
    description: 'Rooted in tradition and shaped with precision, these spaces bring timeless elegance into a contemporary context.',
    designLanguage: ['Rich wood tones', 'Subtle ornamentation', 'Symmetry & proportion'],
    image: 'https://customer-assets.emergentagent.com/job_design-intent/artifacts/p516krn3_classic01.png',
    color: '#D4C4B0',
  },
  {
    id: 4,
    name: 'Urban Luxe',
    tagline: 'Understated. Elevated.',
    description: 'Luxury that doesn\'t announce itself. Clean geometry, deep tones, and refined materials create a composed, confident space.',
    designLanguage: ['Dark accents (charcoal, olive)', 'Stone & metal highlights', 'Minimal yet bold'],
    image: 'https://customer-assets.emergentagent.com/job_design-intent/artifacts/m3bq8n27_Urban_Luxe.png',
    color: '#3D4A3D',
  },
  {
    id: 5,
    name: 'Natural Living',
    tagline: 'Designed to Breathe',
    description: 'Spaces shaped by nature—light, texture, and material. Calm, grounded, and deeply connected to the outside world.',
    designLanguage: ['Organic textures', 'Earthy tones', 'Natural light focus'],
    image: 'https://customer-assets.emergentagent.com/job_design-intent/artifacts/nzg6f9la_Natural_Living.png',
    color: '#A39171',
  },
];

export const MoodsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeMood, setActiveMood] = useState(moods[0]);

  return (
    <section
      ref={ref}
      data-testid="moods-section"
      className="section-padding bg-[#D9CFC0]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-6"
        >
          <span className="overline text-[#B5703A] block mb-4">Design Identities</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2A2A2A] tracking-tight mb-4">
            The 5 Livingcraftsmen Moods
          </h2>
          <p className="text-[#3D3D3D] font-light max-w-xl">
            These are NOT trends. These are lifestyle identities expressed through design.
          </p>
        </motion.div>

        {/* Mood Selector - Desktop */}
        <div className="hidden lg:grid lg:grid-cols-12 gap-8 mt-12">
          {/* Mood List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="col-span-4 space-y-2"
          >
            {moods.map((mood) => (
              <button
                key={mood.id}
                onClick={() => setActiveMood(mood)}
                className={`w-full text-left py-4 px-5 transition-all duration-300 ${
                  activeMood.id === mood.id
                    ? 'bg-[#F5F0E8] border-l-2 border-[#B5703A]'
                    : 'hover:bg-[#F5F0E8]/50'
                }`}
                data-testid={`mood-btn-${mood.id}`}
              >
                <span className="block text-lg font-light text-[#2A2A2A]">
                  {mood.name}
                </span>
                <span className="block text-sm text-[#3D3D3D] opacity-70 mt-1">
                  {mood.tagline}
                </span>
              </button>
            ))}
          </motion.div>

          {/* Active Mood Display */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="col-span-8"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeMood.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-2 gap-6"
              >
                <div className="image-hover-scale aspect-[3/4] bg-[#C4B6A4]">
                  <img
                    src={activeMood.image}
                    alt={activeMood.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col justify-center py-6">
                  <span className="overline text-[#B5703A] mb-3">{activeMood.tagline}</span>
                  <h3 className="text-2xl lg:text-3xl font-light text-[#2A2A2A] mb-4">
                    {activeMood.name}
                  </h3>
                  <p className="text-[#3D3D3D] font-light leading-relaxed mb-6">
                    {activeMood.description}
                  </p>
                  <div>
                    <span className="text-sm font-medium text-[#2A2A2A] mb-3 block">
                      Design Language
                    </span>
                    <ul className="space-y-2">
                      {activeMood.designLanguage.map((item, idx) => (
                        <li key={idx} className="text-sm text-[#3D3D3D] flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#B5703A]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Mobile Mood Cards */}
        <div className="lg:hidden mt-10 space-y-8">
          {moods.map((mood, index) => (
            <motion.div
              key={mood.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-[#F5F0E8] p-6"
              data-testid={`mood-card-mobile-${mood.id}`}
            >
              <div className="image-hover-scale aspect-[16/9] bg-[#C4B6A4] mb-5">
                <img
                  src={mood.image}
                  alt={mood.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="overline text-[#B5703A] text-xs">{mood.tagline}</span>
              <h3 className="text-xl font-light text-[#2A2A2A] mt-2 mb-3">{mood.name}</h3>
              <p className="text-sm text-[#3D3D3D] font-light leading-relaxed mb-4">
                {mood.description}
              </p>
              <ul className="space-y-1">
                {mood.designLanguage.map((item, idx) => (
                  <li key={idx} className="text-xs text-[#3D3D3D] flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B5703A]" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MoodsSection;
